import * as React from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router'
import { Loader2 } from 'lucide-react'
import { tenantLoginInputSchema } from '@lib/input/tenant-auth.input'
import { useTenantAuth } from '@/auth/TenantAuthContext'
import { AuthShell } from '@/components/AuthShell'
import { ErrorState } from '@/components/PageState'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Field } from '@/components/ui/field'
import { isApiError } from '@/helpers/apiClient'
import { apiErrorMessage } from '@/helpers/apiErrorMessage'
import { returnPath } from '@/auth/return-path'

function resolveRedirect<S>(state: S): string {
  return returnPath(state, '/envelopes', (path) => !path.startsWith('/ops'))
}

/** Dashboard sign-in for tenant users. */
export function TenantLoginPage() {
  const { login, isAuthenticated } = useTenantAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  if (isAuthenticated) {
    return <Navigate to={resolveRedirect(location.state)} replace />
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    setError(null)
    const parsed = tenantLoginInputSchema.safeParse({ email, password })
    if (!parsed.success) {
      setError('Enter your email and password.')
      return
    }
    setIsSubmitting(true)
    try {
      await login(parsed.data)
      navigate(resolveRedirect(location.state), { replace: true })
    } catch (failure) {
      setError(
        isApiError(failure) && failure.status === 401
          ? 'Incorrect email or password.'
          : apiErrorMessage(failure, 'Unable to sign in. Please try again.'),
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <AuthShell
      title="Sign in"
      description={
        <>
          Your organization&apos;s PixelSign dashboard. Signing a document? Use the link in your
          email instead.
        </>
      }
    >
      <form className="space-y-4" onSubmit={handleSubmit} noValidate>
        <Field label="Email" htmlFor="email">
          <Input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>
        <Field
          label="Password"
          htmlFor="password"
          labelAside={
            <Link to="/forgot-password" className="text-caption text-link underline-offset-4 hover:underline">
              Forgot password?
            </Link>
          }
        >
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>
        {error ? <ErrorState message={error} /> : null}
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? <Loader2 className="animate-spin" /> : null}
          {isSubmitting ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
    </AuthShell>
  )
}
